import React from 'react';

import { Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useSetRecoilState } from 'recoil';

import { getAxiosErrorOrThrow } from 'shared/utils/error';
import { updateProfile } from 'modules/marketing-profile/api';
import { useGuaranteedAuth } from 'shared/hooks/useAuth';
import { IUpdateMarketingProfile } from 'modules/marketing-profile/models/marketing-profile';
import { registerUser } from 'modules/user/api/user';
import { userInsertSelector } from 'modules/user/state/user-insert';
import {useMarketingProfileAsUser} from 'modules/marketing-profile/hooks/useMarketingProfileAsUser';
import { StudentRegistrationInfo } from 'users/pages/UserRegistration/StudentRegistrationForm/StudentRegistrationInfo';
import { StudentRegistrationExtraInfo } from 'users/pages/UserRegistration/StudentRegistrationForm/StudentRegistrationExtraInfo';

const studentInfoDefaults: IUpdateMarketingProfile = {
    DOB: undefined,
    school: '',
    year: undefined,
    gender: undefined,
    interests: [],
};

export const StudentRegistrationForm = () => {
    const {t} = useTranslation();
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [registrationStep, setRegistrationStep] = React.useState<number>(1);
    const [formData, setFormData] = React.useState<IUpdateMarketingProfile>(studentInfoDefaults);

    const {tokenData, tenantId} = useGuaranteedAuth();

    const profile = useMarketingProfileAsUser(tenantId, tokenData.id);

    const insertUser = useSetRecoilState(userInsertSelector);

    const onSubmit = (data: IUpdateMarketingProfile) => {
        if (!profile?.id || isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        (async () => {
            try {
                await updateProfile(tenantId, profile.id, {...formData, ...data});
                const updatedUser = await registerUser(tenantId, tokenData.id);
                insertUser(updatedUser);
            } catch (e) {
                toast.error(getAxiosErrorOrThrow(e));
            } finally {
                setIsSubmitting(false);
            }
        })();
    };

    const onSetData = (newData: IUpdateMarketingProfile) => {
        setFormData({...formData, ...newData});
    };
    
    return (
        <Container fluid className="d-flex vh-100">
            <Row className="m-auto align-self-center w-100">
                <Col xs={12} md={6} lg={6} className="mx-auto">
                    <h1 style={{textAlign: 'center', marginTop: '20px', marginBottom: '20px'}}>
                        {t('userInfoForm.form.title', 'User Registration Form')}
                    </h1>
                    {registrationStep === 1 ? (
                        <StudentRegistrationInfo
                            isSubmitting={isSubmitting}
                            formData={formData}
                            onSetData={onSetData}
                            onNextStep={() => setRegistrationStep((step) => ++step)}
                        />
                    ) : (
                        <StudentRegistrationExtraInfo
                            isSubmitting={isSubmitting}
                            formData={formData}
                            onSetData={onSetData}
                            onPreviousStep={() => setRegistrationStep((step) => --step)}
                            onSubmit={onSubmit}
                        />
                    )}
                </Col>
            </Row>
        </Container>
    );
};